import { DataProjectModel } from "interfaces/models/dataProjectModel";
import AbstractStore from "stores/AbstractStore";
import { arrayToObject } from "utils/object";
import {
  CacheDataState,
  CacheProjectState,
  CacheStateAction,
  DEFAULT_CACHE_DATA_STATE,
  errorCodes,
  ICacheProjectManager,
  ICacheStorageState,
} from "../types";
import CacheService from "./CacheService";
import CacheStorage from "./CacheStorage";
import CacheProject from "./CacheProject";

class CacheManager
  extends AbstractStore<CacheDataState, CacheStateAction>
  implements ICacheProjectManager
{
  private caches: Record<string, CacheProject> = {};

  constructor() {
    super(DEFAULT_CACHE_DATA_STATE);
    this.init();

    this.pause = this.pause.bind(this);
  }

  private async init() {
    await this.loadProjects();
  }

  public getProjectsState(): CacheProjectState[] {
    return Object.keys(this.caches).map((projectId) => {
      const cache = this.caches[projectId];
      const status = cache.project.getStatus();
      const progress = cache.project.getProgress();
      const lastUpdated = cache.project.getLastUpdated();

      return {
        status,
        progress,
        lastUpdated,
        projectId,
      };
    });
  }

  public dispatch() {
    const projectsState = this.getProjectsState();
    const mapProjectState = arrayToObject(projectsState, "projectId");
    this.setState({
      ...this.state,
      mapProjectState,
    });
  }

  public async pause(id: string) {
    if (!this.isExist(id)) {
      throw new Error(errorCodes.INVALID_PROJECT_ID);
    }

    return this.caches[id].pause();
  }

  public async clear(id: string): Promise<void> {
    if (!this.isExist(id)) {
      throw new Error(errorCodes.INVALID_PROJECT_ID);
    }

    return this.caches[id].clear();
  }

  public async start(id: string): Promise<void> {
    if (!id) {
      throw new Error(errorCodes.INVALID_PROJECT_ID);
    }

    if (!this.isExist(id)) {
      await this.addProject(id);
    }

    return this.caches[id].start();
  }

  public async restart(id: string) {
    await this.clear(id);
    this.start(id);
  }

  protected getProjectById(id: string) {
    return this.caches[id];
  }

  // Add new project and save to indexedDB
  protected async addProject(id: string, defaultState?: ICacheStorageState) {
    const project = new CacheService(id, defaultState);
    if (!defaultState) {
      await project.load();
    }
    this.caches[id] = new CacheProject(project);
    this.caches[id].onDispatch = () => {
      this.dispatch();
    };
    this.dispatch();
  }

  public isExist(id: string) {
    return !!this.caches[id];
  }

  public async loadProjects() {
    const projects = await CacheStorage.getAll();

    // eslint-disable-next-line array-callback-return
    projects?.map((project) => {
      this.addProject(project.id);
    });
  }

  public async stopAll() {
    return Promise.all(
      Object.values(this.caches).map((cache) => {
        return cache.pause();
      })
    );
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new CacheManager();
